import axios, { AxiosError } from 'axios'
import { captureException } from './errorTracking'
import { useHealthStore } from '@/stores/healthStore'

import { API_BASE as BASE_URL } from './apiUrl'
import { getCsrfToken, setCsrfToken } from './csrfToken'
import { loginPathFor } from './authRedirect'
import { lockOfflineCrew } from './offline/storage'

export const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // Send Better Auth session cookies with every request
  timeout: 30000, // 30s default — prevents hung requests from blocking the UI
})

function privateCrewCategory(error: AxiosError): 'private_capture' | 'crew_clock' | null {
  if (!error.config?.url) return null
  try {
    const path = new URL(apiClient.getUri(error.config), 'https://local.invalid/').pathname
    if (path === '/api/crew-private-capture' || path.startsWith('/api/crew-private-capture/')) return 'private_capture'
    if (path === '/api/crew-clock' || path.startsWith('/api/crew-clock/')) return 'crew_clock'
  } catch { /* An invalid URL cannot identify one of these routes. */ }
  return null
}

function reportApiError(error: AxiosError, type: 'network_error' | 'server_error') {
  const category = privateCrewCategory(error)
  if (!category) {
    captureException(error, { type, ...(type === 'server_error' ? { status: error.response?.status } : {}), url: error.config?.url })
    return
  }
  const method = error.config?.method?.toUpperCase()
  const operation = method && ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].includes(method) ? method : 'OTHER'
  const status = error.response?.status
  captureException(new Error('Private crew request failed'), {
    type, category, operation,
    ...(type === 'server_error' && Number.isInteger(status) && status! >= 500 && status! <= 599 ? { status } : {}),
  })
}

// ── CSRF token management ────────────────────────────────────────────────────
// The backend uses double-submit cookie: GET /api/csrf-token sets a _csrf cookie
// and returns the same value in JSON. We must echo it back as x-csrf-token on
// every state-changing request.


// Prevents concurrent 401 responses from all triggering a redirect race
let redirectingTo401 = false

// In-flight deduplication: only one CSRF fetch at a time.
let _csrfInflight: Promise<string> | null = null

async function fetchCsrfToken(): Promise<string> {
  if (_csrfInflight) return _csrfInflight
  _csrfInflight = (async () => {
    try {
      // Bare axios (not apiClient) to avoid the request interceptor re-triggering CSRF refresh.
      // Lower timeout: CSRF should be a fast in-tenant call.
      const res = await axios.get<{ csrfToken: string }>(`${BASE_URL}/api/csrf-token`, {
        withCredentials: true,
        timeout: 10000,
      })
      setCsrfToken(res.data.csrfToken)
      return res.data.csrfToken
    } finally {
      _csrfInflight = null
    }
  })()
  return _csrfInflight
}

/**
 * Pre-warm the CSRF token so the first state-changing request (advance, create,
 * etc.) does not incur an extra network round-trip to /api/csrf-token.  Call
 * this on mount in pages that will fire mutations soon.
 *
 * Idempotent: if the token is already loaded, this resolves immediately.
 * Silently ignores network errors — the request interceptor will retry lazily.
 */
export async function warmCsrf(): Promise<void> {
  if (getCsrfToken()) return
  try { await fetchCsrfToken() } catch { /* silent — interceptor retries lazily */ }
}

// Attach CSRF token to all mutating requests
apiClient.interceptors.request.use(async (config) => {
  const method = (config.method ?? 'get').toUpperCase()
  if (['POST', 'PATCH', 'PUT', 'DELETE'].includes(method)) {
    // Known offline: fail a write immediately with a network error (which
    // describeError() words as "You're offline…") instead of letting it hang
    // for the 30s timeout while the person waits on a spinner (UX-018).
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      throw new AxiosError('Network Error', AxiosError.ERR_NETWORK, config)
    }
    if (!getCsrfToken()) {
      await fetchCsrfToken()
    }
    config.headers['x-csrf-token'] = getCsrfToken()!
  }
  return config
})

// Error interceptor — capture 5xx and network errors; refresh CSRF on 403
apiClient.interceptors.response.use(
  (res) => {
    // Any response proves the device can reach the API (feeds the offline banner).
    useHealthStore.getState().recordSuccess()
    return res
  },
  async (error) => {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        // A 4xx/5xx still means the network works; server faults are shown
        // in-page, never as "Connection lost" (UX-016).
        useHealthStore.getState().recordSuccess()
      } else if (error.code !== AxiosError.ERR_CANCELED) {
        useHealthStore.getState().recordFailure()
      }
      if (!error.response) {
        reportApiError(error, 'network_error')
      } else if (error.response.status === 403 && error.response.data?.error?.includes('CSRF')) {
        // CSRF token expired — refresh and retry exactly once to prevent infinite loop
        type RetryableConfig = typeof error.config & { _csrfRetried?: boolean }
        const cfg = error.config! as RetryableConfig
        if (cfg._csrfRetried) return Promise.reject(error)
        setCsrfToken(null)
        await fetchCsrfToken()
        cfg._csrfRetried = true
        cfg.headers['x-csrf-token'] = getCsrfToken()!
        // Bare axios retry — preserve original timeout if any; default to 30s
        return axios({ ...cfg, timeout: cfg.timeout ?? 30000 })
      } else if (error.response.status === 401) {
        lockOfflineCrew()
        // Auth-namespace endpoints (get-session, me, sign-in) return 401 when
        // unauthenticated — this is expected and must never trigger a hard
        // redirect, since restoreSession fires these in-flight during app boot
        // and a concurrent login can cause the URL to change before they settle.
        const requestUrl = error.config?.url ?? ''
        const isAuthEndpoint = requestUrl.startsWith('/api/auth/')
        // Debounce: only the first concurrent 401 (from app endpoints) redirects
        if (!isAuthEndpoint && !window.location.pathname.startsWith('/login') && !redirectingTo401) {
          redirectingTo401 = true
          // Keep where they were, so signing in again lands back there (UX-063).
          window.location.href = loginPathFor(
            `${window.location.pathname}${window.location.search ?? ""}${window.location.hash ?? ""}`,
          )
        }
      } else if (error.response.status >= 500 && error.response.data?.code !== 'NOT_CONFIGURED') {
        // A 503 NOT_CONFIGURED is an integration nobody has set up yet (the
        // Phone tab asks for the Twilio balance before Twilio is connected),
        // not a server fault, so it stays out of error tracking.
        reportApiError(error, 'server_error')
      }
    }
    return Promise.reject(error)
  }
)

// ── GET deduplication + short-lived cache ────────────────────────────────────
// Prevents duplicate fetches of the same URL within the TTL window and
// deduplicates concurrent in-flight requests for the same URL.
// Call invalidateCache(url) before any mutation that changes that resource.

const _getCache = new Map<string, { data: unknown; expiry: number }>()
const _getInflight = new Map<string, Promise<unknown>>()

export async function cachedGet<T>(url: string, ttlMs = 30_000): Promise<T> {
  const hit = _getCache.get(url)
  if (hit && Date.now() < hit.expiry) return hit.data as T

  const inflight = _getInflight.get(url)
  if (inflight) return inflight as Promise<T>

  const promise = apiClient.get(url)
    .then((res) => {
      _getCache.set(url, { data: res.data, expiry: Date.now() + ttlMs })
      _getInflight.delete(url)
      return res.data as T
    })
    .catch((err) => {
      _getInflight.delete(url)
      throw err
    })

  _getInflight.set(url, promise)
  return promise as Promise<T>
}

export function invalidateCache(url: string) {
  _getCache.delete(url)
}
